import _ from "lodash";
import { useEffect, useState } from "react";
import jsonrepair from "jsonrepair";
import localize from "../../../common/localize";
import Combobox from "../../../components/common/Combobox";
import useGlobalContext from "../../../hooks/useGlobalContext";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import TrueFalseImage from "../../../components/TrueFalseImage";
import { getProfileNameByForm, getActionNameByKey } from "../../../common/utils";
import { getDefaultActions } from "../../../common/EntitiesManagement";

export default function ManageUserProfile() {
  const { userModel, setUserModel, profileData } = useGlobalContext();
  const [userProfile, setUserProfile] = useState(null);
  const [profileModules, setProfileModules] = useState([]);

  const handleUserProfileChange = (profile) => {
    if (!profile) {
      const removedUserRole = _.omit(userModel, "NewRole");
      setUserProfile(null);
      setUserModel({ ...removedUserRole });
      return;
    }
    setUserModel({ ...userModel, NewRole: profile });
    setUserProfile(profile);
  };

  const assignedRole = userModel?.UserRoles?.[0];
  const isUnassign = userModel?.UnassignProfile === true;

  const handleUnassignToggle = (e) => {
    if (e.target.checked) {
      // Pure unassign: drop any pending new-role selection and clear the combobox.
      setUserProfile(null);
      setUserModel({ ..._.omit(userModel, "NewRole"), UnassignProfile: true });
    } else {
      setUserProfile(assignedRole ?? null);
      setUserModel(_.omit(userModel, "UnassignProfile"));
    }
  };

  useEffect(() => {
    if (userModel?.UserRoles?.length > 0) {
      setUserProfile(userModel?.UserRoles?.[0]);
    }
  }, [userModel.Id]);

  useEffect(() => {
    if (userProfile?.Description) {
      try {
        const parsed = JSON.parse(jsonrepair(userProfile.Description));
        const migrated = parsed.map((mod) => {
          if (mod.Restriction === "ACCESS" && (!mod.Actions || !mod.Actions.ACCESS)) {
            return { ...mod, Actions: { ...mod.Actions, ACCESS: true } };
          }
          return mod;
        });
        setProfileModules(migrated);
      } catch {
        setProfileModules([]);
      }
    } else {
      setProfileModules([]);
    }
  }, [userProfile]);

  return (
    <div style={{ width: "100%" }}>
      <form className="w-full mt-2">
        <div className="flex flex-col gap-2 mb-6">
          <Box className="flex flex-col gap-4">
            <Box className="flex gap-2 justify-between">
              <Box className="two_columns_grid">
                <label className="text-xs">
                  <b>{localize.username}:</b>
                </label>
                <label className="text-xs">{userModel.UserName}</label>
                <label className="text-xs">
                  <b>{localize.full_name}:</b>
                </label>
                <label className="text-xs">{userModel.FullName}</label>
              </Box>
            </Box>

            <Alert severity="info" className="w-full">
              <label className="text-xs">
                {localize.profile_assignment_information}
              </label>
            </Alert>

            <Combobox
              isRequired
              id="UserProfile"
              handleValueChange={handleUserProfileChange}
              fieldValue={userProfile ?? null}
              fieldOptions={profileData.Data ?? []}
              fieldLabel={localize.profile}
              customOptionLabel={(option) => `${option.Name}`}
              disabled={isUnassign}
            />

            {assignedRole && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={isUnassign}
                    onChange={handleUnassignToggle}
                  />
                }
                label={
                  <span className="text-xs">
                    {`${localize.unassign_profile} ${assignedRole.Name}`}
                  </span>
                }
              />
            )}

            {profileModules.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1 }}>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#5A6A7A", textTransform: "uppercase" }}>
                  {localize.restrictions}
                </Typography>
                {profileModules.map((moduleObj) => {
                  const activeActions = moduleObj.Actions
                    ? Object.entries(moduleObj.Actions).filter(([, v]) => v).map(([k]) => k)
                    : [];
                  if (activeActions.length === 0) return null;
                  const defaultActions = getDefaultActions();
                  const defaults = activeActions.filter((a) => defaultActions.includes(a));
                  const specials = activeActions.filter((a) => !defaultActions.includes(a));

                  return (
                    <Accordion
                      key={moduleObj.Form}
                      disableGutters
                      sx={{
                        "&:before": { display: "none" },
                        border: "1px solid #E2E8F0",
                        borderRadius: "6px !important",
                        overflow: "hidden",
                        boxShadow: "none",
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={{
                          backgroundColor: "#F8FAFC",
                          minHeight: 36,
                          "& .MuiAccordionSummary-content": {
                            alignItems: "center",
                            gap: 1,
                            my: 0.25,
                          },
                        }}
                      >
                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, flex: 1 }}>
                          {getProfileNameByForm(moduleObj.Form)}
                        </Typography>
                        <Chip
                          size="small"
                          label={activeActions.length === 1 ? `1 ${localize.restriction}` : `${activeActions.length} ${localize.restrictions.toLowerCase()}`}
                          color="warning"
                          variant="outlined"
                          sx={{ fontSize: "0.65rem", height: 20 }}
                        />
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 2, py: 1 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                          {defaults.map((action) => (
                            <Typography key={action} sx={{ fontSize: "0.75rem", color: "#374151" }}>
                              • {getActionNameByKey(action)}
                            </Typography>
                          ))}
                        </Box>
                        {specials.length > 0 && (
                          <>
                            <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "#5A6A7A", textTransform: "uppercase", mt: 0.75 }}>
                              {localize.special_permissions}
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                              {specials.map((action) => (
                                <Typography key={action} sx={{ fontSize: "0.75rem", color: "#374151" }}>
                                  • {getActionNameByKey(action)}
                                </Typography>
                              ))}
                            </Box>
                          </>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            )}
          </Box>
        </div>
      </form>
    </div>
  );
}
